<template>
  <div class="chart-wrapper">
    <div v-if="isChartEmpty || !zonePresent" class="no-data-message">{{ $t('charts.productZones.noData') }}</div>

    <div v-else-if="isLoading" class="loader-overlay"><div class="loader"></div></div>

    <PrioChart
      v-else
      :option="option"
      :active="active"
      :height="320"
      name="ProductDurationDistribution"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import PrioChart from '@/components/ui/PrioChart.vue'
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { useI18n } from 'vue-i18n'
use([BarChart, GridComponent, TooltipComponent, TitleComponent, LegendComponent, CanvasRenderer])

export default {
  name: 'ChartsFixedWidgetsProductPercentageByDuration',
  components: { PrioChart },
  props: {
    percentageByDurationRangeInZone: { type: Object, required: true },
    zonePresent: { type: Boolean, required: true },
    active: { type: Boolean, default: true }
  },
  setup(props){
    const { t } = useI18n()
    const isLoading = ref(true)
    const palette = ['#425461','#4F7EB3','#B2C149','#9FC8E3','#6C9046','#6B92C6']
    // Data keys AND the legend: they read the same in Dutch ('sec', 'min').
    const ranges = ['0-5 sec','6-15 sec','16-60 sec','1-2 min','2-5 min','5+ min']

    const labels = computed(()=> Object.keys(props.percentageByDurationRangeInZone || {}))

    const datasets = computed(()=> ranges.map((r,i)=>({
      name:r, stack:'p', type:'bar',
      data: labels.value.map(z => Math.round(props.percentageByDurationRangeInZone?.[z]?.[r] || 0)),
      itemStyle:{ color: palette[i % palette.length] }
    })))

    const isChartEmpty = computed(()=> !labels.value.length || datasets.value.every(ds => ds.data.every(v=>!v)))

    const option = computed(()=>({
      title:{ text:t('charts.productZones.distributionTitle'), left:'center', textStyle:{color:'grey',fontSize:12}},
      legend:{ show:true, orient:'vertical', right:10, top:'middle' },
      grid:{ containLabel:true, top:'20%', height:'80%', right:'20%' },
      tooltip:{ trigger:'axis', axisPointer:{ type:'shadow' }, formatter: ps => ps.map(p=>`${p.seriesName}: ${p.data}%`).join('<br>') },
      xAxis:{ type:'category', data:labels.value, axisLabel:{ rotate:45, fontSize:10 } },
      yAxis:{ type:'value', min:0, max:100, axisLabel:{ formatter:v=>v+'%' }, name:t('charts.productZones.percentageAxis') },
      series: datasets.value
    }))

    onMounted(()=>{ isLoading.value=false })
    watch(()=>props.percentageByDurationRangeInZone, ()=>{ isLoading.value=false })

    return { isLoading, option, isChartEmpty }
  }
}
</script>

<style scoped>
.chart-wrapper{ width:100%; position:relative; }
.no-data-message{ color:gray; text-align:center; margin:20px 0 }
.loader-overlay{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; background:rgba(255,255,255,.8)}
.loader{ width:24px; aspect-ratio:1; border-radius:50%; background:#B2C149; animation:spin 1s linear infinite }
@keyframes spin{ to{ transform:rotate(1turn) } }
</style>